import React from "react";
import { render } from "react-dom";

import { StatefulEvent } from "Global/Base/Events";
import { Globalization } from "Global/Dom/Globalization";
import { registerCustomElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { DataAttributesProps, LoadableEditableControlComponentProps } from "UI/UI.React/components/types";
import { DataConverter } from "../../../Global/Base/Converters/DataConverter";
import { EditableControlConnector } from "../EditableControlConnector";
import { GenericControlConnectorAttrs } from "../GenericControlConnector";

/**
 * Umožňuje napojení react komponent do původní multiweb aplikace.
 * Do konektoru je skrze konstruktor vložena RichTextEdit komponenta, které jsou následně předány props důležité pro její vykreslování.
 * Konektor implementuje stejná rozhraní jako editovatelný control {@link EditableControl} a navíc jeste informaci o tom, zda je komponenta nacitana.
 * Konektor obsahuje dva typy IDček viz {@link __dataKey} a {@link GenericControlConnector#_componentId|_componentId}.
 */
export class RichTextEditConnector extends EditableControlConnector {
    private _isLoading: boolean = true;
    private _fakeLoaderElement: FakeDOMLoaderElement;
    private _clickedEvent: (e: MouseEvent) => void;

    public override get recommended(): boolean {
        return super.recommended;
    }

    public override set recommended(value: boolean) {
        super.recommended = value;
        this._rootElement.classList.toggle("Recommended", value);
    }

    constructor(attrs: GenericControlConnectorAttrs, canRender: boolean = true) {
        super(attrs, canRender);

        this.element.classList.add("richtextedit-connector");
        this._fakeLoaderElement = new FakeDOMLoaderElement();

        this._rootElement.insertAdjacentElement("beforebegin", this._fakeLoaderElement);
        //this._fakeLoaderElement.appendChild(this._rootElement);

        this._clickedEvent = (e: MouseEvent) => {
            this.clicked.trigger(this, e);
        };

        this._fakeLoaderElement.connected.add(() => {
            this._isLoading = false;
            this._render();
            this._rootElement.addEventListener("click", this._clickedEvent);
        });
    }

    /**
     * Zkonvertuje atributy ovlivňující podobu editovatelné vykreslované react komponenty do props.
     * Přidává i zparsované hodnoty z data atributů rootElementu.
     * @private
     */
    protected override _getRenderProps(): RichTextEditProps & LoadableEditableControlComponentProps & DataAttributesProps {
        const availableFeatures: Record<string, Partial<IFeature>> = {
            "undo": {
                toolbar: "undo redo",
            },
            "textdecoration": {
                toolbar: "bold italic underline strikethrough",
            },
            "textstyle": {
                toolbar: "fontfamily fontsize blocks",
            },
            "color": {
                toolbar: "forecolor backcolor",
            },
            "align": {
                toolbar: "alignleft aligncenter alignright alignjustify",
            },
            "indent": {
                toolbar: "outdent indent",
            },
            "lists": {
                toolbar: "numlist bullist",
                plugins: "lists advlist",
            },
            "subscript": {
                toolbar: "subscript superscript",
            },
            "table": {
                toolbar: "table",
                plugins: "table",
            },
            "image": {
                toolbar: "image",
                plugins: "image",
            },
            "emoticons": {
                toolbar: "emoticons",
                plugins: "emoticons",
            },
            "link": {
                toolbar: "link",
                plugins: "link autolink",
            },
            "html": {
                toolbar: "code",
                plugins: "code",
            },
            "search": {
                toolbar: "searchreplace",
                plugins: "searchreplace",
            },
            "removeformat": {
                toolbar: "removeformat",
            },
            "fullscreen": {
                toolbar: "fullscreen",
                plugins: "fullscreen",
            },
            "print": {
                toolbar: "preview print",
                plugins: "preview",
            },
            "code": {
                toolbar: "codesample",
                plugins: "codesample",
            },
            "wordcount": {
                plugins: "wordcount",
            },
            "clipboard": {
                toolbar: "copy cut paste",
            },
            "quote": {
                toolbar: "blockquote",
            },
        };

        let rawFeatures = this.element?.getAttribute("data-toolbarfeatures");
        let plugins = this.element?.getAttribute("data-plugins");

        let features = "";

        if (!rawFeatures) {
            rawFeatures = "Undo | TextDecoration | TextStyle Color | Align | Image Link | FullScreen";
        }

        if (!plugins) {
            plugins = "";
        }

        let blockOfFeatures: string[] = [];
        const transformedFeatures: string[] = [];

        rawFeatures.toLocaleLowerCase().split("|").forEach((separatedFeatures) => {
            separatedFeatures.trim().split(" ").forEach((feature) => {
                const availableFeature = availableFeatures[feature];

                if (!availableFeature) {
                    return;
                }

                const toolbar = availableFeature.toolbar;
                const plugin = availableFeature.plugins;

                if (toolbar) {
                    blockOfFeatures.push(toolbar);
                }
                if (plugin && !plugins.includes(plugin)) {
                    plugins += " " + plugin;
                }
            });

            transformedFeatures.push(blockOfFeatures.join(" "));
            blockOfFeatures = [];
        });

        features = transformedFeatures.join(" | ");

        return {
            ...super._getRenderProps(),
            isLoading: this._isLoading ?? true,
            language: Globalization.actualCultureISO639_1,
            showMenuBar: DataConverter.toBoolean(this.element?.getAttribute("data-showmenubar"), true),
            plugins: plugins,
            toolbar: features,
        };
    }

    protected override _render(): void {
        const genericComponentProps = this._getRenderProps();
        render(
            <this._rootComponent>
                <this._component {...genericComponentProps} />
            </this._rootComponent>,

            this._rootElement,
        );
    }

    public override dispose(): void {
        super.dispose();

        this._rootElement.removeEventListener("click", this._clickedEvent);
    }
}

/**
 * Tento CustomElement je zde proto, aby podaval skutecnou informaci o tom, kdy je komponenta skutecne pripojena k hlavnimu DOM.
 * Je to nutne proto, ze tinyMCE.js nefunguje korektne pokud je volana inicializace driv nez je element, ke kteremu se to vztahuje pripojen do hlavniho DOM
 */
export class FakeDOMLoaderElement extends HTMLElement {
    public connected: StatefulEvent<FakeDOMLoaderElement, undefined>;
    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("fake-dom-loader");
        this.connected = new StatefulEvent();
    }

    protected connectedCallback(): void {
        this.connected.trigger(this, undefined);
    }
}

interface IFeature {
    toolbar: string;
    plugins: string;
}

type RichTextEditProps = {
    toolbar?: string;
    plugins?: string;
    language?: string;
    showMenuBar?: boolean;
};

registerCustomElement("tsc-FakeDOMLoader", FakeDOMLoaderElement);
